// core/lib/profileFields.ts — client-side mirror of backend/apps/accounts/profile_fields.py
// for the Edit Profile page (1.27.70). The server is the authority and refuses the
// whole save on any bad field; these checks exist so the page can point at the
// field first instead of failing the whole form.
import { PROFILE_LIMITS, type BirthdayShown, type ProfileLink } from '@/core/models/account';
import type { IconName } from '@/icons/names';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Adds https:// when a scheme is missing ("example.org" → "https://example.org"). */
export function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/** True for an http(s) URL with a host and no spaces, within the server's length cap. */
export function isWebAddress(href: string): boolean {
  if (!href || href.length > PROFILE_LIMITS.linkHref || /\s/.test(href)) return false;
  try {
    const url = new URL(href);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname !== '';
  } catch {
    return false;
  }
}

/** "https://www.example.org/a" → "example.org". Falls back to the raw text. */
export function hostnameOf(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return href.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
  }
}

/** What a link row shows: its label, or the site's name when it has none. */
export function linkText(link: ProfileLink): string {
  return link.label.trim() || hostnameOf(link.href);
}

/** A recognisable mark for the few sites we have one for; the generic link otherwise. */
export function linkIcon(href: string): IconName {
  const host = hostnameOf(href).toLowerCase();
  if (host === 'x.com' || host === 'twitter.com' || host.endsWith('.x.com')) return 'logo-x';
  if (host.endsWith('facebook.com')) return 'logo-facebook';
  if (host.endsWith('reddit.com')) return 'logo-reddit';
  if (host === 't.me' || host.endsWith('telegram.org')) return 'logo-telegram';
  return 'link';
}

/** Whole years between an ISO birthday and `today` (both YYYY-MM-DD, local). */
export function ageOn(birthday: string, today: Date): number {
  const [y = 0, m = 0, d = 0] = birthday.split('-').map(Number);
  const years = today.getFullYear() - y;
  const beforeBirthday =
    today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d);
  return beforeBirthday ? years - 1 : years;
}

/** null when the birthday is fine (or empty); otherwise the message to show under the field. */
export function birthdayProblem(birthday: string, today: Date = new Date()): string | null {
  if (!birthday) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthday);
  if (!match) return 'Enter a real date.';
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return 'Enter a real date.';
  }
  if (year < 1900 || date > today) return 'Enter a real date.';
  if (ageOn(birthday, today) < PROFILE_LIMITS.minAge) {
    return `TruXSocial is for people aged ${PROFILE_LIMITS.minAge} and over.`;
  }
  return null;
}

/** "August 12" or "August 12, 1990". */
export function formatBirthday(shown: BirthdayShown): string {
  const month = MONTHS[shown.month - 1] ?? '';
  return shown.year ? `${month} ${shown.day}, ${shown.year}` : `${month} ${shown.day}`;
}

/**
 * What other people will see of a birthday under a visibility choice: the same
 * cut the server makes in serializers.serialize_user. Used by the live preview,
 * so it shows exactly what the saved profile will.
 */
export function shownBirthday(
  birthday: string,
  visibility: 'private' | 'month_day' | 'full',
): BirthdayShown | undefined {
  if (!birthday || visibility === 'private') return undefined;
  const [year, month, day] = birthday.split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return visibility === 'full' ? { month, day, year } : { month, day };
}
